/**
 * Feedback Context Utility
 *
 * Helper functions for collecting context data for feedback submissions
 * Handles page type detection, URL sanitization, and session ID extraction
 */

// Route segment indices for URL parsing
const ROUTE_SEGMENT = {
  BASE: 0, // First segment (e.g., 'agents', 'workspaces')
  AGENT_NAME: 1, // Agent name in /agents/:agentName
  SESSION_ID: 2, // Session ID in /agents/:agentName/:sessionId
} as const;

// Route path identifiers
const ROUTE_PATH = {
  AGENTS: 'agents',
  WORKSPACES: 'workspaces',
} as const;

// Minimum segment counts for route patterns
const MIN_SEGMENTS = {
  AGENT_CATALOG: 1, // /agents
  CHAT: 2, // /agents/:agentName (with or without session)
  CHAT_WITH_SESSION: 3, // /agents/:agentName/:sessionId
} as const;

/**
 * Extract page type from URL string
 *
 * @param url - URL string
 * @returns Page type: 'workspace-launcher' | 'agent-catalog' | 'chat'
 */
export function extractPageTypeFromUrl(
  url: string,
): 'workspace-launcher' | 'agent-catalog' | 'chat' {
  const pathOnly = url.split('?')[0];
  const segments = pathOnly.split('/').filter(Boolean);

  if (
    segments.length === 0 ||
    segments[ROUTE_SEGMENT.BASE] === ROUTE_PATH.WORKSPACES
  ) {
    return 'workspace-launcher';
  } else if (
    segments[ROUTE_SEGMENT.BASE] === ROUTE_PATH.AGENTS &&
    segments.length === MIN_SEGMENTS.AGENT_CATALOG
  ) {
    return 'agent-catalog';
  } else if (
    segments[ROUTE_SEGMENT.BASE] === ROUTE_PATH.AGENTS &&
    segments.length >= MIN_SEGMENTS.CHAT
  ) {
    return 'chat';
  }

  return 'workspace-launcher';
}

/**
 * Extract sanitized URL from URL string
 * Removes session_id and other personal data from the path
 *
 * @param url - URL string
 * @returns Sanitized URL path without session_id or query parameters
 */
export function extractSanitizedUrlFromString(url: string): string {
  const pathOnly = url.split('?')[0];
  const segments = pathOnly.split('/').filter(Boolean);

  // If URL matches /agents/:agentName/:sessionId pattern, remove sessionId
  if (
    segments.length >= MIN_SEGMENTS.CHAT_WITH_SESSION &&
    segments[ROUTE_SEGMENT.BASE] === ROUTE_PATH.AGENTS
  ) {
    return `/${segments[ROUTE_SEGMENT.BASE]}/${segments[ROUTE_SEGMENT.AGENT_NAME]}`; // /agents/:agentName
  }

  // Ensure we always return a valid path (at least '/')
  return pathOnly || '/';
}

/**
 * Extract session ID from URL string
 * Pattern: /agents/:agentName/:sessionId
 *
 * @param url - URL string
 * @returns Session ID string or undefined if not present
 */
export function extractSessionIdFromUrl(url: string): string | undefined {
  const pathOnly = url.split('?')[0];
  const segments = pathOnly.split('/').filter(Boolean);

  // If URL matches /agents/:agentName/:sessionId pattern
  if (
    segments.length >= MIN_SEGMENTS.CHAT_WITH_SESSION &&
    segments[ROUTE_SEGMENT.BASE] === ROUTE_PATH.AGENTS
  ) {
    return segments[ROUTE_SEGMENT.SESSION_ID]; // Third segment is sessionId
  }

  return undefined;
}
